import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { HomePack } from "./types";
import { PackTierCard } from "./PackTierCard";
import { PackVisual } from "../fx/PackVisual";
import { TierBadge } from "../storefront/TierBadge";
import { rm } from "../../lib/format";
import { sfx } from "../../lib/sfx";
import { usePrefersReducedMotion } from "../../hooks/useBreakpoint";

function circularDistance(index: number, selected: number, length: number) {
  const raw = Math.abs(index - selected);
  return Math.min(raw, length - raw);
}

/**
 * Pack shop: a row of fixed-size selector cards drives a centred coverflow
 * carousel underneath — drag it, use the arrows, or tap a card in the row to
 * bring that pack to the middle, where it pops forward while its neighbours
 * shrink and fade away.
 */
export function PackCoverflow({ packs }: { packs: HomePack[] }) {
  const reduced = usePrefersReducedMotion();
  const loop = packs.length > 1;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    align: "center",
    // Default trimSnaps drops extra snaps when slides fit on screen (common with 2 packs).
    containScroll: "keepSnaps",
  });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    setSelected((i) => Math.min(i, Math.max(0, packs.length - 1)));
  }, [packs.length]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect, packs.length]);

  if (!packs.length) return null;
  const focused = packs[selected] ?? packs[0]!;

  function goTo(i: number) {
    const next = ((i % packs.length) + packs.length) % packs.length;
    sfx.tap();
    setSelected(next);
    emblaApi?.scrollTo(next);
  }

  return (
    <div className="space-y-5">
      {/* fixed-size selector row */}
      <div className="scroll-row -mx-4 min-w-0 max-w-full px-4 pb-1 pt-1 lg:mx-0 lg:justify-center lg:px-0">
        {packs.map((pack, i) => (
          <PackTierCard key={pack.id} pack={pack} active={i === selected} onClick={() => goTo(i)} />
        ))}
      </div>

      {/* coverflow — capped width so the focused pack sits dead-centre of the
          page instead of stretching edge to edge on wide screens */}
      <div className="relative mx-auto max-w-2xl">
        <div className="touch-pan-y overflow-hidden py-6" ref={emblaRef}>
          <div className="flex">
            {packs.map((pack, i) => {
              const d = circularDistance(i, selected, packs.length);
              const focusedSlide = d === 0;
              const scale = focusedSlide ? 1 : d === 1 ? 0.8 : 0.64;
              const opacity = focusedSlide ? 1 : d === 1 ? 0.55 : 0.25;
              const lift = focusedSlide ? -8 : 0;
              return (
                <div
                  key={pack.id}
                  className="min-w-0 flex-[0_0_80%] px-2 sm:flex-[0_0_68%] lg:flex-[0_0_58%]"
                >
                  <div
                    role="button"
                    tabIndex={focusedSlide ? -1 : 0}
                    aria-label={focusedSlide ? `${pack.name} selected` : `Select ${pack.name}`}
                    onClick={() => {
                      if (!focusedSlide) goTo(i);
                    }}
                    onKeyDown={(e) => {
                      if (!focusedSlide && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        goTo(i);
                      }
                    }}
                    className={`mx-auto max-w-[220px] ${focusedSlide ? "" : "cursor-pointer"} ${reduced ? "" : "transition-[transform,opacity] duration-300 ease-out"}`}
                    style={{ transform: `translateY(${lift}px) scale(${scale})`, opacity, zIndex: 10 - d }}
                  >
                    <PackVisual
                      id={pack.id}
                      name={pack.name}
                      images={pack.images}
                      foilLabel={pack.foilLabel}
                      foilColor={pack.foilColor}
                      sweep={focusedSlide}
                      className="aspect-[5/7] w-full shadow-2xl"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {packs.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous pack"
              onClick={() => goTo(selected - 1)}
              className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white/80 backdrop-blur transition-colors hover:bg-black/60 hover:text-white lg:-left-10"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next pack"
              onClick={() => goTo(selected + 1)}
              className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white/80 backdrop-blur transition-colors hover:bg-black/60 hover:text-white lg:-right-10"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>

      {/* purchase panel for the centred pack */}
      <div className="glass-panel mx-auto flex max-w-2xl flex-wrap items-center justify-between gap-3 rounded-2xl p-4">
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-bold">{focused.name}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-white/50">
            {focused.topTier && (
              <TierBadge name={focused.topTier} color={focused.topTierColor ?? undefined} glow size="sm" />
            )}
            <span>{focused.totalRemaining} cards left</span>
          </div>
        </div>
        <Link
          to={`/packs/${focused.id}`}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-gradient-brand px-5 py-2.5 text-xs font-bold shadow-lg shadow-brand-pink/25 transition-transform hover:scale-105"
        >
          Rip it open · {rm(focused.price)}
        </Link>
      </div>
    </div>
  );
}
